// Sprint Hub -- NotificationPanel
// Dropdown panel (360px wide) showing recent notifications.
// Grouped by today / yesterday / older.
// ASCII only -- no unicode or special characters.

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markNotificationRead } from '../lib/api.js';

// ---------------------------------------------------------------------------
// Type icons (SVG, inline) -- ASCII labels only
// ---------------------------------------------------------------------------
const TYPE_ICONS = {
  approval_needed: ApprovalIcon,
  approval_completed: CheckIcon,
  client_alert: AlertIcon,
  governance_deadline: CalendarIcon,
  pipeline_update: PipelineIcon,
  brief_ready: BriefIcon,
  team_signal: TeamIcon,
  system: SystemIcon,
};

const TYPE_COLORS = {
  approval_needed:    'var(--color-accent)',
  approval_completed: '#16a34a',
  client_alert:       '#dc2626',
  governance_deadline:'#d97706',
  pipeline_update:    '#7c3aed',
  brief_ready:        'var(--color-accent)',
  team_signal:        '#db2777',
  system:             'var(--color-muted-text)',
};

// ---------------------------------------------------------------------------
// Time helpers -- ASCII only
// ---------------------------------------------------------------------------
function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 7)  return d + 'd ago';
  return new Date(isoStr).toLocaleDateString();
}

function dayGroup(isoStr) {
  if (!isoStr) return 'older';
  const d = new Date(isoStr);
  const now = new Date();
  const todayStr = now.toDateString();
  const yesterdayStr = new Date(now - 86400000).toDateString();
  if (d.toDateString() === todayStr) return 'today';
  if (d.toDateString() === yesterdayStr) return 'yesterday';
  return 'older';
}

// ---------------------------------------------------------------------------
// NotificationPanel
// Props:
//   onClose         -- fn() called to close the panel
//   onMarkAllRead   -- fn() called when "Mark all read" is pressed (Bell resets count)
//   onReadOne       -- fn() called when a single notification is marked read (Bell decrements count)
// Fetches its own notification list on mount. Handles mark-read internally.
// Outside-click and Escape are handled by NotificationBell (parent).
// ---------------------------------------------------------------------------
export default function NotificationPanel({ onClose, onMarkAllRead, onReadOne }) {
  var navigate = useNavigate();
  var panelRef = useRef(null);

  var _notifs = useState([]);
  var notifications = _notifs[0]; var setNotifications = _notifs[1];
  var _loading = useState(true);
  var loading = _loading[0]; var setLoading = _loading[1];

  // Fetch notifications on mount
  useEffect(function() {
    setLoading(true);
    getNotifications({ limit: 30 })
      .then(function(res) {
        if (res && res.data && res.data.items) setNotifications(res.data.items);
      })
      .catch(function() {})
      .finally(function() { setLoading(false); });
  }, []);

  function handleItemClick(notif) {
    if (!notif.is_read) {
      // Optimistic update
      setNotifications(function(prev) {
        return prev.map(function(n) { return n.id === notif.id ? Object.assign({}, n, { is_read: 1 }) : n; });
      });
      markNotificationRead(notif.id).catch(function() {});
      if (onReadOne) onReadOne();
    }
    if (notif.action_url) {
      onClose();
      if (notif.action_url.startsWith('/')) {
        navigate(notif.action_url);
      } else {
        window.open(notif.action_url, '_blank', 'noopener');
      }
    }
  }

  // Group notifications
  var groups = { today: [], yesterday: [], older: [] };
  notifications.forEach(function(n) {
    var g = dayGroup(n.created_at);
    groups[g].push(n);
  });

  var unreadCount = notifications.filter(function(n) { return !n.is_read; }).length;
  var isEmpty = !loading && notifications.length === 0;

  return (
    <div ref={panelRef} style={styles.panel} role="dialog" aria-label="Notifications" aria-modal="true">
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.headerTitle}>Notifications</span>
        <div style={styles.headerActions}>
          {unreadCount > 0 && (
            <button style={styles.markAllBtn} onClick={function() {
              setNotifications(function(prev) {
                return prev.map(function(n) { return Object.assign({}, n, { is_read: 1 }); });
              });
              if (onMarkAllRead) onMarkAllRead();
            }} aria-label="Mark all as read">
              Mark all read
            </button>
          )}
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close notifications">
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {loading && (
          <div style={styles.emptyState}>
            <div style={styles.spinner} aria-label="Loading" />
          </div>
        )}

        {isEmpty && (
          <div style={styles.emptyState}>
            <BellEmptyIcon />
            <p style={styles.emptyText}>No notifications yet</p>
          </div>
        )}

        {!loading && !isEmpty && (
          <>
            {groups.today.length > 0 && (
              <NotifGroup label="Today" items={groups.today} onItemClick={handleItemClick} />
            )}
            {groups.yesterday.length > 0 && (
              <NotifGroup label="Yesterday" items={groups.yesterday} onItemClick={handleItemClick} />
            )}
            {groups.older.length > 0 && (
              <NotifGroup label="Older" items={groups.older} onItemClick={handleItemClick} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotifGroup
// ---------------------------------------------------------------------------
function NotifGroup({ label, items, onItemClick }) {
  return (
    <div style={styles.group}>
      <div style={styles.groupLabel}>{label}</div>
      {items.map(function(notif) {
        return <NotifItem key={notif.id} notif={notif} onClick={onItemClick} />;
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotifItem
// ---------------------------------------------------------------------------
function NotifItem({ notif, onClick }) {
  const IconComponent = TYPE_ICONS[notif.type] || SystemIcon;
  const iconColor = TYPE_COLORS[notif.type] || 'var(--color-muted-text)';

  return (
    <button
      style={{
        ...styles.item,
        ...(notif.is_read ? {} : styles.itemUnread),
      }}
      onClick={function() { onClick(notif); }}
      aria-label={notif.title}
    >
      {/* Unread dot */}
      {!notif.is_read && <div style={styles.unreadDot} aria-hidden="true" />}

      {/* Icon */}
      <div style={{ ...styles.iconWrap, color: iconColor }}>
        <IconComponent />
      </div>

      {/* Content */}
      <div style={styles.content}>
        <div style={styles.itemTitle}>{notif.title}</div>
        {notif.body && (
          <div style={styles.itemBody}>{notif.body}</div>
        )}
        <div style={styles.itemMeta}>
          {notif.entity_id && (
            <span style={styles.entityPill}>{notif.entity_id.replace(/_/g, ' ')}</span>
          )}
          <span style={styles.timeAgo}>{timeAgo(notif.created_at)}</span>
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  panel: {
    position: 'absolute',
    top: 'calc(var(--layout-topbar-height) + 4px)',
    right: '0.75rem',
    width: 360,
    maxHeight: 520,
    background: 'var(--color-nav-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: '0.75rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1200,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid var(--color-border)',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: 'var(--color-foreground)',
    letterSpacing: '-0.01em',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  markAllBtn: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--color-accent)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem 0.375rem',
    borderRadius: '0.375rem',
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-muted-text)',
    borderRadius: '0.375rem',
  },
  body: {
    overflowY: 'auto',
    flex: 1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2.5rem 1.5rem',
    gap: '0.75rem',
  },
  emptyText: {
    fontSize: '0.875rem',
    color: 'var(--color-muted-text)',
    margin: 0,
  },
  spinner: {
    width: 28,
    height: 28,
    borderRadius: '9999px',
    border: '3px solid var(--color-muted-bg)',
    borderTopColor: 'var(--color-accent)',
    animation: 'spin 0.8s linear infinite',
  },
  group: {
    paddingBottom: '0.25rem',
  },
  groupLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--color-muted-text)',
    padding: '0.625rem 1rem 0.375rem',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    width: '100%',
    padding: '0.625rem 1rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    position: 'relative',
    transition: 'background 120ms ease',
    borderRadius: 0,
  },
  itemUnread: {
    background: 'rgba(var(--color-accent-rgb, 59,130,246), 0.04)',
  },
  unreadDot: {
    position: 'absolute',
    top: '1.1rem',
    left: '0.4rem',
    width: 6,
    height: 6,
    borderRadius: '9999px',
    background: 'var(--color-accent)',
    flexShrink: 0,
  },
  iconWrap: {
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: '0.5rem',
    background: 'var(--color-muted-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '0.125rem',
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: 'var(--color-foreground)',
    lineHeight: 1.35,
    marginBottom: '0.1875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemBody: {
    fontSize: '0.75rem',
    color: 'var(--color-muted-text)',
    lineHeight: 1.45,
    marginBottom: '0.3125rem',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  itemMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  entityPill: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--color-accent)',
    background: 'rgba(var(--color-accent-rgb, 59,130,246), 0.1)',
    padding: '0.125rem 0.375rem',
    borderRadius: '0.25rem',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  },
  timeAgo: {
    fontSize: '0.6875rem',
    color: 'var(--color-muted-text)',
  },
};

// ---------------------------------------------------------------------------
// Icons -- all SVG, 16x16
// ---------------------------------------------------------------------------
function ApprovalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12l2 2 4-4"/>
      <circle cx="12" cy="12" r="10"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <triangle points="12 2 22 20 2 20"/>
      <path d="M12 8v4M12 16h.01"/>
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

function PipelineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}

function BriefIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}

function TeamIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
    </svg>
  );
}

function BellEmptyIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
      stroke="var(--color-muted-text)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      opacity="0.4">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
